import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useState } from 'react';

import { useI18n } from '../../../i18n/I18nProvider';
import { qk } from '../../../lib/queryKeys';
import { notificacionesApi } from '../api/notificaciones.api';

export function NotificacionesBell({ tone = 'dark' }: { tone?: 'dark' | 'light' }): JSX.Element {
  const es = useI18n();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [soloNoLeidas, setSoloNoLeidas] = useState(true);

  const { data: contador } = useQuery({
    queryKey: qk.notificaciones.contador,
    queryFn: () => notificacionesApi.contador(),
    refetchInterval: 60_000, // 60s
  });

  const { data: lista, refetch } = useQuery({
    queryKey: qk.notificaciones.list({ soloNoLeidas }),
    queryFn: () => notificacionesApi.list({ soloNoLeidas, limit: 30 }),
    enabled: open,
  });

  const mutationLeida = useMutation({
    mutationFn: (id: string) => notificacionesApi.marcarLeida(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.notificaciones.contador });
      void refetch();
    },
  });
  const mutationTodas = useMutation({
    mutationFn: () => notificacionesApi.marcarTodasLeidas(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.notificaciones.contador });
      void refetch();
    },
  });

  const cuenta = contador?.noLeidas ?? 0;
  const notificaciones = lista?.notificaciones ?? [];

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={es.notificaciones.abrirPanel}
        onClick={() => setOpen((v) => !v)}
        className={
          tone === 'light'
            ? 'relative rounded-md p-2 text-white hover:bg-white/15'
            : 'relative rounded-md p-2 text-text-marca hover:bg-marca-neutro/60'
        }
      >
        <Bell size={18} />
        {cuenta > 0 && (
          <span
            className="absolute right-0.5 top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white"
            title={`${String(cuenta)} ${es.notificaciones.contadorTooltip}`}
          >
            {cuenta > 99 ? '99+' : cuenta}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={es.notificaciones.titulo}
          className="absolute right-0 z-50 mt-2 w-96 rounded-md border border-marca-neutro bg-surface p-3 shadow-lg"
        >
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-semibold text-text-marca">{es.notificaciones.titulo}</h3>
            <div className="flex gap-2 text-xs">
              <button
                type="button"
                className={
                  soloNoLeidas
                    ? 'rounded bg-marca-neutro px-2 py-0.5'
                    : 'px-2 py-0.5 text-marca-gris/70 hover:underline'
                }
                onClick={() => setSoloNoLeidas(true)}
              >
                {es.notificaciones.soloNoLeidas}
              </button>
              <button
                type="button"
                className={
                  !soloNoLeidas
                    ? 'rounded bg-marca-neutro px-2 py-0.5'
                    : 'px-2 py-0.5 text-marca-gris/70 hover:underline'
                }
                onClick={() => setSoloNoLeidas(false)}
              >
                {es.notificaciones.todas}
              </button>
            </div>
          </div>

          {cuenta > 0 && (
            <button
              type="button"
              className="mb-2 w-full rounded border border-marca-neutro px-2 py-1 text-xs text-text-marca hover:bg-marca-neutro/60"
              disabled={mutationTodas.isPending}
              onClick={() => mutationTodas.mutate()}
            >
              {es.notificaciones.marcarTodasLeidas}
            </button>
          )}

          <ul className="flex max-h-80 flex-col gap-2 overflow-auto">
            {notificaciones.length === 0 ? (
              <li className="py-4 text-center text-xs text-marca-gris/60">{es.notificaciones.vacio}</li>
            ) : (
              notificaciones.map((n) => (
                <li
                  key={n.id}
                  className={`rounded-md border p-2 text-xs ${
                    n.leidoEn ? 'border-marca-neutro bg-surface' : 'border-info bg-info-light/40'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="font-semibold text-text-marca">{n.titulo}</div>
                      <p className="mt-0.5 line-clamp-3 text-marca-gris/80">{n.cuerpo}</p>
                      <div className="mt-1 text-[10px] text-marca-gris/60">
                        {new Date(n.creadoEn).toLocaleString('es-AR', {
                          timeZone: 'America/Argentina/Buenos_Aires',
                        })}
                      </div>
                    </div>
                    {!n.leidoEn && (
                      <button
                        type="button"
                        className="rounded border border-marca-neutro px-1 py-0.5 text-[10px] hover:bg-marca-neutro/60"
                        disabled={mutationLeida.isPending}
                        onClick={() => mutationLeida.mutate(n.id)}
                      >
                        {es.notificaciones.marcarLeida}
                      </button>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
